<template>
  <div class="quiz-brag__container" :class="{'--dark-theme': isTextColourLight}">
    <vue-glide
      :key="sliderComponentUpdateKey"
      :perView="1"
      :bullet="true"
      :autoplay="4500"
      v-model="activeSlide"
      @glide:move-after="onChangeSlide"
      class="glide__container"
    >
      <vue-glide-slide>
        <QuizStatics
          :image="rankingImage"
          :heading="rankingHeading"
          :textHTML="isTeamQuiz ? rankingTeamHTML : rankingSoloHTML"
        />
      </vue-glide-slide>
      <vue-glide-slide v-if="isShowPercentile">
        <QuizStatics
          :image="percentileImage"
          :heading="percentileHeading"
          :emojiHeading="isEmojiHeadingPercentileVisible ? '🤗' : null"
          :textHTML="isTeamQuiz ? percentileTeamHTML : percentileSoloHTML"
        />
      </vue-glide-slide>
      <vue-glide-slide v-if="isShowCorrectRate">
        <QuizBragCorrectRate
          :audience="data.audience"
          :shareType="shareType"
          :totalQuizCount="parseInt(data.totalQuiz, 10)"
          :isTeamQuiz="isTeamQuiz"
          :roundedCorrectRate="roundedCorrectRate"
        />
      </vue-glide-slide>
      <vue-glide-slide v-if="isShowSayThanks">
        <QuizStatics
          :image="sayThanksImage"
          :heading="sayThanksHeading"
          :textHTML="sayThanksHTML"
        />
      </vue-glide-slide>
      <template slot="control">
        <button data-glide-dir="<" class="glide__controls__button-left">
          <aha-icon name="arrow-no-tail" class="glide__controls__arrow-left" />
        </button>
        <button data-glide-dir=">" class="glide__controls__button-right">
          <aha-icon name="arrow-no-tail" />
        </button>
      </template>
    </vue-glide>

    <p v-if="showSharing" class="quiz-sharing__header">
      {{ $t('shareThisOn') }}
    </p>

    <QuizSharing
      v-if="showSharing"
      :position="data.index"
      :correctRate="roundedCorrectRate"
      :percentile="percentileNumber"
      :total="totalCount"
      :is-team="isTeamQuiz? true : false"
      :shareType="shareType"
      :presenterName="presenterName"
      :presentationName="presentationName"
      class="quiz-sharing__container"
    />
  </div>
</template>
<script>
import { mapState } from 'vuex'
import Config from '@/config'
import { Glide, GlideSlide } from 'vue-glide-js'
import Util from '@/util'
import * as Sentry from '@sentry/vue'

// components
import QuizSharing from '@/components/QuizSharing.vue'
import QuizStatics from '@/components/quiz/QuizStatics.vue'
import QuizBragCorrectRate from '@/components/quiz/QuizBragCorrectRate.vue'

// images
const rankingImage1 = require('@/assets/images/quiz-brag/trophy1.svg')
const rankingImage2 = require('@/assets/images/quiz-brag/trophy2.svg')
const rankingImage3 = require('@/assets/images/quiz-brag/trophy3.svg')
const rankingImage4 = require('@/assets/images/quiz-brag/trophy4.svg')
const percentileImage = require('@/assets/images/quiz-brag/percentile.svg')
const sayThanksImage = require('@/assets/images/quiz-brag/thanks.svg')

export default {
  components: {
    QuizSharing,
    QuizStatics,
    QuizBragCorrectRate,
    [Glide.name]: Glide,
    [GlideSlide.name]: GlideSlide,
  },
  props: {
    isTeamQuiz: {
      type: Boolean,
      default: false,
    },
    data: {
      type: Object,
      default: () => {},
    },
    teams: {
      type: Array,
      default: () => [],
    },
    total: {
      type: Number,
      default: null,
    },
    percentile: {
      type: Number,
      default: null,
    },
    totalTeam: {
      type: Number,
      default: null,
    },
    percentileTeam: {
      type: Number,
      default: null,
    },
    rankingWithinTeam: {
      type: Number,
      default: null,
    },
    percentileWithinTeam: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      rankingImage1,
      rankingImage2,
      rankingImage3,
      rankingImage4,
      percentileImage,
      sayThanksImage,
      activeSlide: 0,
      showSharing: false,
      sliderComponentUpdateKey: 0,
    }
  },
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      slideActive: (state) => state.slide,
    }),
    // Images
    rankingImage() {
      let rankingImage = this.rankingImage4
      if (this.data.index === 1) {
        rankingImage = this.rankingImage1
      }
      if (this.data.index === 2) {
        rankingImage = this.rankingImage2
      }
      if (this.data.index === 3) {
        rankingImage = this.rankingImage3
      }
      return rankingImage
    },

    // Headings
    rankingHeading() {
      return this.data.index < 4 ? `${this.$t('congratulations')}!` : `${this.$t('quizCompleted')}!`
    },

    percentileHeading() {
      if (!this.percentileNumber) return ''
      if (this.percentileNumber <= 10) {
        return this.$t('amazing')
      }
      if (this.percentileNumber > 10 && this.percentileNumber <= 25) {
        return this.$t('greatWork')
      }
      if (this.percentileNumber > 25 && this.percentileNumber < 49) {
        return this.$t('goodJob')
      }
      return this.$t('betterLuckNextTime')
    },

    presentationName() {
      return this.presentation.name
    },

    sayThanksHeading() {
      return `${this.$t('thisQuizWasMadeBy', {
        name: this.presenterName,
      })}`
    },

    // Texts
    yourFinalScoreIsText() {
      return this.hasTotalPoint ? `${this.$t('yourFinalScoreIs', {
        msg: `<strong>${Util.formatNumber(this.data.audience.totalPoint)} ${this.$t('points')}</strong>`,
      })}` : ''
    },
    youAreInPlaceText() {
      if (!this.data.indexFormat || this.data.indexFormat === 0) {
        return `<p>${this.$t('haventAnsweredAnyQuiz')}</p>`
      }
      return `<p>${this.$t(
        'youAreInPlace',
        {
          msg: `<strong>${this.data.indexFormat || 0}</strong>`,
          msg1: `<strong>${this.data.audiencesCount || 0}</strong>`,
        },
      )}!</p>
      <p>${this.yourFinalScoreIsText}</p>`
    },
    youAreInPlaceTextEnglish() {
      if (!this.data.index) {
        return `<p>${this.$t('haventAnsweredAnyQuiz')}</p>`
      }
      return `<p>You got
              <strong>${Util.toEnglishRankingSuffix(this.data.index || 0)}</strong>
              place out of <strong>${this.data.audiencesCount}</strong> players!</p>
              <p>${this.yourFinalScoreIsText}</p>`
    },
    rankingSoloHTML() {
      if (this.presentation.language === 'en') {
        return this.youAreInPlaceTextEnglish
      }
      return this.youAreInPlaceText
    },

    yourTeamFinalScoreIsText() {
      return this.hasTotalPoint ? `${this.$t('yourTeamScoreIs', {
        msg: `<strong>${this.data.audience.totalPoint}</strong>`,
      })} ${this.$t('points')}.` : ''
    },

    yourTeamInPlaceText() {
      if (!this.data.index || this.data.index === 0) {
        return `<p>${this.$t('teamHasntAnsweredAnyQuiz')}</p>`
      }
      return `<p>${this.$t('yourTeamInPlaceTeams',
        {
          msg: `<strong>${this.data.indexFormat || 0}</strong>`,
          num: `<strong>${this.data.audiencesCount || 0}</strong>`,
        })}</p>
      <p>${this.yourTeamFinalScoreIsText}</p>`
    },

    yourTeamInPlaceTextEnglish() {
      if (!this.data.index || this.data.index === 0) {
        return `<p>${this.$t('teamHasntAnsweredAnyQuiz')}</p>`
      }
      return `<p>Your team came <strong>${Util.toEnglishRankingSuffix(this.data.index || 0)}</strong>
        out of <strong>${this.teams.length || 0}</strong> teams.</p>
              <p>${this.yourTeamFinalScoreIsText}</p>`
    },

    rankingTeamHTML() {
      if (this.presentation.language === 'en') {
        return this.yourTeamInPlaceTextEnglish
      }
      return this.yourTeamInPlaceText
    },

    percentileSoloHTML() {
      if (!this.percentileNumber) return ''

      return `<p>${this.$t('youFinishedInThe')} <strong>${this.$t('topPercentage', {
        number: Util.roundNumberWithNDecimals(this.percentileNumber, 0, true) || 0,
      })}</strong> ${this.$t('ofAllPlayers')}.</p>`
    },

    percentileTeamHTML() {
      if (!this.percentileNumber) return ''

      return `<p>${this.$t('yourTeamFinishedInThe')} <strong>${this.$t('topPercentage', {
        number: Util.roundNumberWithNDecimals(this.percentileNumber, 0, true) || 0,
      })}</strong> ${this.$t('ofAllTeams')}.</p>`
    },

    sayThanksHTML() {
      return `<p>${this.$t('dontForgetToShowSupport')}</p>`
    },

    roundedCorrectRate() {
      if (!this.data.audience) return 0
      const { audience } = this.data
      return (audience.correctRate && audience.correctRate * 100) || 0
    },

    isSkoleTube() {
      try {
        const { identifyProvider } = this.presentation.user
        return identifyProvider === 'skoletube'
      } catch (error) {
        Sentry.withScope((scope) => {
          scope.setExtra('presentation', this.presentation)
          scope.setExtra('leaderboardData', this.data)
          Sentry.captureException(error)
        })
        return false
      }
    },

    slideMap() {
      const slideMap = ['ranking']
      if (this.isShowPercentile) {
        slideMap.push('percentile')
      }
      if (this.isShowCorrectRate) {
        slideMap.push('correct-rate')
      }
      if (this.isShowSayThanks) {
        slideMap.push('say-thanks')
      }
      return slideMap
    },

    shareType() {
      return this.slideMap[this.activeSlide]
    },

    isTextColourLight() {
      return this.slideActive.textColour === '#ffffff'
    },

    presenterName() {
      try {
        const { firstName, lastName } = this.presentation.user
        return `${firstName} ${lastName}`.trim()
      } catch (e) {
        return ''
      }
    },

    percentileNumber() {
      const percentile = this.isTeamQuiz ? this.percentileTeam : this.percentile
      if (percentile >= 0) return (1 - percentile) * 100
      return null
    },

    totalCount() {
      return this.isTeamQuiz ? this.teams.length : this.data.audienceCount
    },

    isShowPercentile() {
      if (!this.percentileNumber) return false
      return this.totalCount > 10
    },

    isShowCorrectRate() {
      return true
    },

    isShowSayThanks() {
      return this.showSharing && !this.isSkoleTube
    },

    isEmojiHeadingPercentileVisible() {
      if (this.percentileNumber && this.percentileNumber >= 49) return true
      return false
    },

    hasTotalPoint() {
      return this.data && this.data.audience && this.data.audience.totalPoint
    },
  },
  mounted() {
    if (window.location.host === Config.domain) {
      this.showSharing = true
      this.sliderComponentUpdateKey++ // force rerender so slider bullets update correctly
    }
  },

  methods: {
    onChangeSlide() {
      Util.gaTrackEvent(
        'Audience',
        'Quiz Brag > Change slide',
        this.shareType,
      )
    },
  },
}
</script>
<style lang="scss" scoped>
.quiz-brag__container {
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  margin-bottom: 1.5rem;
  text-align: center;
  &.--dark-theme {
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(20px);
    .glide__container {
      & ::v-deep {
        .glide__bullet {
          background-color: rgba(255, 255, 255, 0.4);
        }
        .glide__bullet--active {
          background-color: #ffffff;
        }
        .glide__controls__button-left, .glide__controls__button-right {
          color: rgba(255, 255, 255, 0.65);
        }
      }
    }
  }
  .winner__container {
    padding-top: 10px;
  }
}
.quiz-sharing__header {
  opacity: 0.6;
}
.quiz-sharing__container {
  padding: 10px 10px 20px 10px;
  max-width: 260px;
  margin: 0 auto;
}
.glide__container {
  position: relative;
  .glide__controls__arrow-left {
    transform: rotate(180deg);
  }
  .glide__controls__button-left, .glide__controls__button-right {
    background-color: transparent;
    color: #999999; // base-70
    border: none;
    cursor: pointer;
  }
  & ::v-deep {
    .glide__slides {
      margin-bottom: 5px;
    }
     div[data-glide-el="controls"] {
        position: absolute;
        width: 50%;
        left: 24.5%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        top: 22%;
        @media (max-width: 767px) { // md
          width: 94%;
          left: 3.5%;
        }
      }
    .glide__bullet {
      background-color: rgba(0, 0, 0, 0.3);
      border: 0;
      outline: 0;
      width: 8px;
      height: 8px;
      border-radius: 20px;
      cursor: pointer;
      padding: 0;
      &:not(:first-child) {
        margin-left: 10px;
      }
      &.glide__bullet--active {
        background-color: #4B4B4B; // base-90
      }
    }
  }
}
</style>
